import {
  type LessonItem,
  canJoinLesson,
  canOpenClassBoard,
  simUrl,
  suggestSims,
  useMaterialActions,
  useClassTests,
  useLessonPlan,
  shortName,
  timeOf,
  todayIso,
  useLessonAdminActions,
  useTeachers,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { FlaskConical } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import {
  GRAD,
  type Grad,
  INK,
  INK_SOFT,
  PAPER,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  grad,
  pillClass,
} from '../design/forms';
import { ArrowUpRightIcon, WarningCircleIcon } from '../design/icons';
import { buttonClass } from '../design/ui';
import { Badge, Button, Field, Modal, Segmented, Select } from './ui';

/*
 * The lessons of the classic week (the teacher's and the class's schedule, the home of the staff) in
 * «Форми 2» (28.10): a lesson is a soft card in its subject's gradient with the subject's picture big and
 * quiet behind, the words in the colour's ink; the marks (a test, online, the shelter, a substitute) are
 * soft rectangles, white on the colour; the way into the lesson is a pill with a ringed ↗.
 */

export type LessonPerspective = 'student' | 'teacher' | 'class' | 'school';

/** A mark on a lesson's coloured card: white, the words in a colour. */
function OnColor({ children, ink = INK }: { children: ReactNode; ink?: string }) {
  return (
    <span
      className="inline-flex h-[22px] shrink-0 items-center rounded-[7px] bg-white/75 px-1.5 text-[11.5px] leading-none font-medium whitespace-nowrap"
      style={{ color: ink }}
    >
      {children}
    </span>
  );
}

export function LessonBadges({
  lesson,
  memberId,
  onColor = false,
}: {
  lesson: LessonItem;
  memberId?: string;
  /** on the lesson's coloured card: white marks */
  onColor?: boolean;
}) {
  const { t } = useTranslation();
  const marks: Array<{ key: string; tone: Grad; ink?: string; text: string }> = [];
  if (lesson.status === 'cancelled')
    marks.push({ key: 'cancelled', tone: 'coral', text: t('schedule.cancelled') });
  if (lesson.isTest && lesson.status !== 'cancelled')
    marks.push({ key: 'test', tone: 'coral', ink: '#D9471A', text: t('schedule.test') });
  if (lesson.format === 'online')
    marks.push({ key: 'online', tone: 'lavender', text: t('format.online') });
  if (lesson.format === 'shelter')
    marks.push({ key: 'shelter', tone: 'butter', text: t('format.shelter') });
  if (lesson.substitute)
    marks.push({
      key: 'substitute',
      tone: 'cobalt',
      text:
        lesson.substitute.id === memberId
          ? t('schedule.substituting')
          : t('schedule.substitute', { name: shortName(lesson.substitute) }),
    });
  if (marks.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {marks.map((m) =>
        onColor ? (
          <OnColor key={m.key} ink={m.ink ?? GRAD[m.tone].ink}>
            {m.text}
          </OnColor>
        ) : (
          <span
            key={m.key}
            className="inline-flex h-6 items-center rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
            style={{ backgroundImage: grad(m.tone), color: m.ink ?? GRAD[m.tone].ink }}
          >
            {m.text}
          </span>
        ),
      )}
    </span>
  );
}

/** One lesson as a compact soft card; what the second line shows depends on who is looking. */
export function LessonCard({
  lesson,
  perspective,
  memberId,
  onClick,
  showTime = false,
}: {
  lesson: LessonItem;
  perspective: LessonPerspective;
  memberId?: string;
  onClick?: () => void;
  showTime?: boolean;
}) {
  const { t } = useTranslation();
  // families follow the lesson from home; the video room is for the class and the school
  const guardian = useCurrentContext().kind === 'guardian';
  const teacher = lesson.substitute ?? lesson.teachers[0];
  const cancelled = lesson.status === 'cancelled';
  const look = subjectLook(lesson.subject);
  const tone: Grad = cancelled ? 'stone' : SUBJECT_GRAD[look.tone];
  // the teacher reads the class first: the subject is theirs, the class is where to go
  const classFirst = perspective === 'teacher';
  const title = classFirst
    ? `${lesson.classLabel}${lesson.groupLabel ? ` · ${lesson.groupLabel}` : ''}`
    : lesson.subject;
  const details = [
    classFirst ? lesson.subject : perspective === 'school' ? lesson.classLabel : null,
    perspective !== 'teacher' && teacher ? shortName(teacher) : null,
    classFirst ? null : lesson.groupLabel,
    // numbered rooms read «каб. 203»; named ones («Спортзал») stay as they are
    lesson.room
      ? /^\d/.test(lesson.room)
        ? t('schedule.room', { room: lesson.room })
        : lesson.room
      : null,
  ].filter(Boolean);
  const join = !onClick && !guardian && canJoinLesson(lesson, todayIso());
  const board = !onClick && !guardian && canOpenClassBoard(lesson, todayIso());
  const body = (
    <>
      <SubjectArt
        icon={look.icon}
        tone={tone}
        size={showTime ? 76 : 62}
        ghost
        className={`pointer-events-none absolute -z-10 ${showTime ? 'top-1/2 right-16 -translate-y-[42%]' : '-right-2 -bottom-3'}`}
      />
      <span className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span
            className={`block text-[14.5px] leading-5 font-medium ${cancelled ? 'line-through' : ''}`}
            style={{ color: GRAD[tone].ink }}
          >
            {title}
          </span>
          {details.length > 0 && (
            <span className="block text-[12px] leading-4" style={{ color: INK_SOFT }}>
              {details.join(' · ')}
            </span>
          )}
        </span>
        {showTime && (
          <span className="flex shrink-0 flex-col items-end leading-tight" style={{ color: INK }}>
            <span className="font-display text-[16px] font-light tracking-[-0.02em]">
              {timeOf(lesson.startsAt)}
            </span>
            <span className="text-[11.5px]" style={{ color: INK_SOFT }}>
              {timeOf(lesson.endsAt)}
            </span>
          </span>
        )}
      </span>
      {(cancelled || lesson.isTest || lesson.format !== 'in_person' || lesson.substitute) && (
        <span className="mt-1.5 block">
          <LessonBadges lesson={lesson} onColor {...(memberId !== undefined && { memberId })} />
        </span>
      )}
      {(join || board) && (
        <span className="mt-2 flex flex-wrap gap-1.5">
          {join && (
            <Link
              to="/lesson/$lessonId"
              params={{ lessonId: lesson.id }}
              className={pillClass('dark', 'sm')}
            >
              {perspective === 'teacher' ? t('lesson.start') : t('lesson.join')}
              <PillIcon icon={ArrowUpRightIcon} size="sm" />
            </Link>
          )}
          {board && (
            <Link
              to="/class-board/$lessonId"
              params={{ lessonId: lesson.id }}
              className={pillClass(join ? 'light' : 'dark', 'sm')}
            >
              {perspective === 'teacher' ? t('classBoard.lead') : t('classBoard.open')}
              <PillIcon icon={ArrowUpRightIcon} size="sm" />
            </Link>
          )}
        </span>
      )}
    </>
  );
  const className = `relative isolate block w-full overflow-hidden rounded-[16px] px-3 py-2.5 text-left ${cancelled ? 'opacity-60' : ''}`;
  const style = { backgroundImage: grad(tone) };
  return onClick ? (
    <button
      type="button"
      className={`${className} outline-none transition-[filter] hover:brightness-[0.97] focus-visible:ring-2 focus-visible:ring-[var(--sh-dark)]`}
      style={style}
      onClick={onClick}
    >
      {body}
    </button>
  ) : (
    <div className={className} style={style}>
      {body}
    </div>
  );
}

/** A small name over a part of a dialog. */
function Label({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-[13px] font-medium" style={{ color: INK_SOFT }}>
      {children}
    </p>
  );
}

/** Administration: format, cancellation and the teacher of one lesson. Changes save at once. */
export function LessonManageModal({
  lesson,
  schoolId,
  onClose,
}: {
  lesson: LessonItem;
  schoolId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useLessonAdminActions();
  const teachers = useTeachers(schoolId);
  const regular = lesson.teachers.map((p) => p.id);
  const cancelled = lesson.status === 'cancelled';
  return (
    <Modal
      title={`${lesson.subject} · ${lesson.classLabel}${lesson.groupLabel ? ` · ${lesson.groupLabel}` : ''}`}
      onClose={onClose}
    >
      <p className="-mt-2 mb-5 text-[14px]" style={{ color: INK_SOFT }}>
        {t('schedule.period', { n: lesson.period })}, {timeOf(lesson.startsAt)}–
        {timeOf(lesson.endsAt)}
        {lesson.topic ? ` · ${lesson.topic}` : ''}
      </p>
      <div className="flex flex-col gap-5">
        <div>
          <Label>{t('schedule.manage.format')}</Label>
          <Segmented
            value={lesson.format}
            onChange={(format) => update.mutate({ lessonId: lesson.id, format })}
            options={(['in_person', 'online', 'shelter'] as const).map((f) => ({
              value: f,
              label: t(`format.${f}`),
            }))}
          />
        </div>
        <div>
          <Label>{t('schedule.manage.substitute')}</Label>
          <Select
            className="w-full"
            value={lesson.substitute?.id ?? ''}
            onChange={(e) =>
              update.mutate({ lessonId: lesson.id, substituteMemberId: e.target.value || null })
            }
          >
            <option value="">{t('schedule.manage.noSubstitute')}</option>
            {(teachers.data ?? [])
              .filter((p) => !regular.includes(p.id))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.last_name} {p.first_name} {p.middle_name ?? ''}
                </option>
              ))}
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {cancelled && <Badge tone="danger">{t('schedule.cancelled')}</Badge>}
          <button
            type="button"
            className={buttonClass('outline')}
            style={cancelled ? undefined : { color: GRAD.coral.ink }}
            onClick={() =>
              update.mutate({
                lessonId: lesson.id,
                status: cancelled ? 'scheduled' : 'cancelled',
              })
            }
          >
            {cancelled ? t('schedule.manage.restore') : t('schedule.manage.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Simulations of the lab that fit the topic: one click puts one into the lesson's materials. */
function LabSuggestions({ lesson, topic }: { lesson: LessonItem; topic: string }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = useMaterialActions(ctx.schoolId, ctx.memberId);
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
  const sims = suggestSims({ subject: lesson.subject, topic, grade: lesson.grade }).slice(0, 3);
  if (sims.length === 0) return null;
  return (
    <div className="rounded-[18px] p-3.5" style={{ backgroundColor: PAPER }}>
      <p className="mb-2 flex items-center gap-2 text-[14px] font-medium" style={{ color: INK }}>
        <span
          aria-hidden
          className="flex size-7 items-center justify-center rounded-[9px]"
          style={{ backgroundImage: grad('aqua'), color: GRAD.aqua.ink }}
        >
          <FlaskConical size={15} strokeWidth={1.8} />
        </span>
        {t('lab.suggest.title')}
      </p>
      <ul className="flex flex-col gap-1.5">
        {sims.map((s) => (
          <li key={s.slug} className="flex items-center justify-between gap-2 text-[14px]">
            <Link
              to="/lab/sim/$slug"
              params={{ slug: s.slug }}
              target="_blank"
              className="min-w-0 underline decoration-[#E4E0D7] underline-offset-4 hover:decoration-current"
              style={{ color: INK }}
            >
              {s.title}
            </Link>
            {added.has(s.slug) ? (
              <span
                className="shrink-0 rounded-[7px] px-2 py-1 text-[12px] leading-none font-medium"
                style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
              >
                {t('lab.suggest.added')}
              </span>
            ) : (
              <button
                type="button"
                className={buttonClass('outline', 'sm')}
                onClick={() =>
                  void actions.addLink
                    .mutateAsync({
                      courseId: lesson.courseId,
                      lessonId: lesson.id,
                      title: s.title,
                      url: simUrl(s.slug),
                    })
                    .then(() => setAdded(new Set([...added, s.slug])))
                }
              >
                {t('lab.suggest.add')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The teacher plans one of their lessons: the topic, what to bring, whether it is a test — with
 * the class's other tests that day, so a third one is a conscious choice.
 */
export function LessonPlanModal({ lesson, onClose }: { lesson: LessonItem; onClose: () => void }) {
  const { t } = useTranslation();
  const plan = useLessonPlan();
  const [topic, setTopic] = useState(lesson.topic ?? '');
  const [bring, setBring] = useState(lesson.bring ?? '');
  const [isTest, setIsTest] = useState(lesson.isTest);
  const tests = useClassTests(lesson.classId, lesson.date);
  const others = (tests.data ?? []).filter((l) => l.id !== lesson.id);
  const subjects = others.map((l) => l.subject).join(', ');
  return (
    <Modal title={`${lesson.subject} · ${lesson.classLabel}`} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void plan.mutateAsync({ lessonId: lesson.id, topic, bring, isTest }).then(onClose);
        }}
      >
        <p className="-mt-2 text-[14px]" style={{ color: INK_SOFT }}>
          {t('schedule.period', { n: lesson.period })}, {timeOf(lesson.startsAt)}–
          {timeOf(lesson.endsAt)}
        </p>
        <Field
          id="plan-topic"
          label={t('journal.editTopic')}
          value={topic}
          maxLength={300}
          onChange={(e) => setTopic(e.target.value)}
        />
        <Field
          id="plan-bring"
          label={t('journal.bring')}
          placeholder={t('journal.bringHint')}
          value={bring}
          maxLength={200}
          onChange={(e) => setBring(e.target.value)}
        />
        <LabSuggestions lesson={lesson} topic={topic} />
        <label
          className="flex cursor-pointer items-center gap-2.5 text-[14px]"
          style={{ color: INK }}
        >
          <input
            type="checkbox"
            checked={isTest}
            onChange={(e) => setIsTest(e.target.checked)}
            className="size-[18px] accent-[var(--sh-dark)]"
          />
          {t('journal.isTest')}
        </label>
        {isTest && others.length >= 2 && (
          <p
            className="flex items-start gap-2 rounded-[16px] px-3.5 py-3 text-[14px] leading-5"
            style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
          >
            <WarningCircleIcon size={18} weight="fill" aria-hidden className="mt-px shrink-0" />
            <span style={{ color: INK }}>
              {t('journal.testsTooMany', {
                class: lesson.classLabel,
                count: others.length,
                subjects,
              })}
            </span>
          </p>
        )}
        {isTest && others.length === 1 && (
          <p className="text-[13.5px]" style={{ color: INK_SOFT }}>
            {t('journal.testsOne', { class: lesson.classLabel, subjects })}
          </p>
        )}
        <div>
          <Button type="submit" className="!w-auto px-6" disabled={plan.isPending}>
            {t('journal.editor.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
